<template>
  <div>
    <b-modal
      id="otonomoSubcountryModal"
      size="lg"
      content-class="menu-bg-blue-transparent-modal"
      centered
      title="Otonomo Subcountries"
      hide-footer

      @shown="createMap()"
      @hide="closeOtonomo()"
    >
      <b-container>
        <div>
          <div
            v-if="view.getZoom() < 9"
            class="text-center"
          >
            <fa
              :icon="['fas', 'exclamation-triangle']"
              size="3x"
              class="collapsed-yellow"
            />
            ZOOM IN
          </div>
          <div class="otonomo-controls-row">
            <b-form-checkbox v-model="maintainMapView" switch>
              Maintain current map view
            </b-form-checkbox>
            <span v-if="selectedSubcountry" class="selected-subcountry">
              Selected Subcountry: {{selectedSubcountry.toUpperCase()}}
            </span>
            <span v-else class="selected-subcountry">
              No Subcountry Selected
            </span>
          </div>
        </div>
        <div
          id="otonomoMap"
          class="ol-map otonomo-map"
          :style="otonomoMapReady ? 'visibility: visible' : 'visibility: hidden'"
        ></div>
      </b-container>
    </b-modal>
  </div>
</template>

<style scoped>
.otonomo-map {
  height: 80vh !important;
  width: 100%;
}
.otonomo-controls-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 0.5rem;
}

.selected-subcountry {
  color: inherit;
}
</style>

<script>
import axios from 'axios'
import {createLayers} from '../../../utils/utils.js'
import Attribution from 'ol/control/Attribution'
import {getCenter} from 'ol/extent'
import GeoJSON from 'ol/format/GeoJSON'
import {get as projGet, transform, transformExtent} from 'ol/proj'
import Map from 'ol/Map'
import OSM from 'ol/source/OSM'
import TileLayer from 'ol/layer/Tile'
import VectorSource from 'ol/source/Vector'
import View from 'ol/View'
export default {
  name: 'MMP_ANSI_Otonomo',
  data() {
    return {
      otonomoLayer: null,
      otonomoMap: null,
      otonomoMapReady: false,
      selectedSubcountry: null,
      // When true, selecting a subcountry leaves the main map's current
      // view alone instead of zooming/panning to the state's center.
      maintainMapView: false
    }
  },
  props: [
    'previousSelectedSubcountry',
    'view'
  ],
  methods: {
    closeOtonomo() {
      this.otonomoMapReady = false
    },
    createMap() {
      this.otonomoMap = new Map({
        controls: [new Attribution({collapsible: true})],
        layers: [
          new TileLayer({
            source: new OSM(),
            name: 'OSM base layer',
            zIndex: 2,
            opacity: 0
          })
        ],
        target: 'otonomoMap',
        view: new View({
          center: new transform(
            this.view.getCenter(),
            new projGet('EPSG:4326'),
            new projGet('EPSG:3857')
          ),
          zoom: 4,
          minZoom: 4,
          multiWorld: true,
          constrainOnlyCenter: true
        })
      })

      // Load previously selected subcountry if one exists
      if (this.previousSelectedSubcountry) {
        this.selectedSubcountry = this.previousSelectedSubcountry
      }

      this.getSubcountryLayer()
    },
    getSubcountryLayer() {
      if (sessionStorage.token) {
        axios
        .get(
          process.env.API_LOCATION + '/api/layer/getSubcountryLayer',
          {
            headers: {
              'x-auth-token': sessionStorage.token
            }
          }
        )
        .then(async (response) => {
          const content = response.data
          if (content.errors) {
            console.log('Otonomo Subcountry API Call Error: ' + content.errors)
          } else {
            let layerLinks = []
            content.rows
              .filter(e => e.visibility != false)
              .forEach(link => {
                let linkObj = link.link_object
                // Replaces all underscores with spaces
                let output = linkObj.name.replace(/_/g, ' ')
                linkObj['display_name'] = output
                layerLinks.push(linkObj)
              })
            this.otonomoLayer = await createLayers(layerLinks, this.otonomoMap).otonomo_subcountries
            this.zoomToFitFeatures(this.otonomoLayer.layer)
            this.otonomoMap.on('singleclick', evt => {
              this.querySiteOtonomo(evt, this.otonomoLayer.layer, this.otonomoMap, 'mmp:otonomo_subcountries_new')
            })

            // If there is a previously selected subcountry, highlight it
            let layerSource = this.otonomoMap.getLayers().getArray()[1].getSource()
            // Pass in values for GeoServer SLD variable substitution
            layerSource.updateParams({
              Env: `subcountry:${this.selectedSubcountry};selectedFill:FFFFFF;selectedStroke:21D886;`
            })
          }
        })
        .catch(function (error) {
          console.log(error)
        })
      } else {
        console.log('No User Token')
      }
    },
    querySiteOtonomo(evt, layer, map, geoserverName) {
      const source = layer.getSource()
      const viewResolution = map.getView().getResolution()
      let url = source.getFeatureInfoUrl(evt.coordinate, viewResolution, 'EPSG:3857', {
        INFO_FORMAT: 'application/json',
        LAYERS: geoserverName,
        CQL_FILTER: 'INCLUDE',
        QUERY_LAYERS: geoserverName
      }) +
      '&FEATURE_COUNT=100&buffer=5&format_options=callback:getJsonFeature&authkey='+sessionStorage.token

      if (sessionStorage.token) {
        axios
          .get(url, {})
          .then(response => {
            let features = response.data.features
            if (!features || !features.length) {
              console.log('No feature at this location')
              return
            }

            let feature = features.find(f => f.properties.visible) || features[0]
            let zoomFeature = features.find(f => f.properties.visible === false) || feature

            let newSubcountry = feature.properties.name
            let sameSubcountrySelected =
              this.selectedSubcountry
              && this.selectedSubcountry.toUpperCase() == newSubcountry.toUpperCase()
            let layerSource = this.otonomoMap.getLayers().getArray()[1].getSource()

            this.selectedSubcountry = sameSubcountrySelected ? null : newSubcountry

            layerSource.updateParams({
              Env: `subcountry:${this.selectedSubcountry};selectedFill:FFFFFF;selectedStroke:21D886;`
            })

            if (!sameSubcountrySelected && zoomFeature.geometry && !this.maintainMapView) {
              let geometry = new GeoJSON().readGeometry(zoomFeature.geometry)
              this.view.animate({
                center: getCenter(geometry.getExtent()),
                zoom: 9.5,
                duration: 750
              }, () => {
                this.$emit('enableOtonomoLayer', this.selectedSubcountry)
              })
            } else {
              this.$emit('enableOtonomoLayer', this.selectedSubcountry)
            }

            if (!sameSubcountrySelected) {
              this.$bvModal.hide('otonomoSubcountryModal')
            }
          })
          .catch(function (error) {
            console.log(error)
          })
      } else {
        console.log('No User Token')
      }
    },
    zoomToFitFeatures(layer) {
      let layerSource = layer.getSource()
      let baseUrl = layerSource.getUrls()[0].split('service=')[0]
      let url =
        baseUrl +
        'service=WFS&request=GetFeature&typeName=mmp:otonomo_subcountries_new&outputFormat=application/json&format_options=callback:getJsonFeatureExtent&authkey='+sessionStorage.token
      if (sessionStorage.token) {
        axios
        .get(url, {})
        .then(response => {
          let features = new GeoJSON().readFeatures(response.data)
          if (features.length != 0) {
            let fittedExtent = new transformExtent(
              new VectorSource({features: features}).getExtent(),
              new projGet('EPSG:4326'),
              new projGet('EPSG:3857')
            )
            let fittedPadding = 5
            let fittedOptions = {
              padding: [
                fittedPadding,
                fittedPadding,
                fittedPadding,
                fittedPadding
              ],
              callback: (complete) => {
                this.otonomoMapReady = true
              }
            }
            this.otonomoMap.setView(new View({
              center: this.otonomoMap.getView().getCenter(),
              extent: fittedExtent,
              zoom: 4,
              minZoom: 4,
              multiWorld: true,
              constrainOnlyCenter: true
            }))
            this.otonomoMap.getView().fit(fittedExtent, fittedOptions)
          }
        })
        .catch(function (error) {
          console.log(error)
        })
      } else {
        console.log('No User Token')
      }
    }
  },
  watch: {
    selectedSubcountry: function (newValue, oldValue) {
      this.$emit('subcountryChanged')
    }
  }
}
</script>
